// import { Fragment } from 'react';
// import {
//   Box,
//   Typography,
// } from '@mui/material';

// import DynamicAdSense from './DynamicAdSense';

// const AdsDemo = ({ startingTabValue = 0 }) => {
//   return (
//     <Fragment key={`post-fragment`}>
//       <Box
//         className='meme'
//         sx={{
//           width: '100%',
//           height: '100%',
//           minHeight: '100%',
//           maxHeight: '100%',
//           overflow: 'hidden',
//           maxWidth: '100dvw',
//           scrollSnapAlign: 'start',
//           scrollSnapStop: 'always',
//           display: 'flex',
//           flexDirection: 'column',
//           justifyContent: 'center',
//           px: { xs: 1.5, md: 0 },
//         }}
//       >
//         <Box
//           sx={{
//             width: '100%',
//             maxHeight: '100%',
//             overflow: 'auto',
//             maxWidth: '100dvw',
//             display: 'flex',
//             flexDirection: 'column',
//             px: { xs: 2, md: 4 },
//           }}
//         >
//           <Typography variant="h6" sx={{ mb: 2, px: 2 }}>
//             About Soopra
//           </Typography>

//           <Typography variant="h7" component="div" sx={{ mb: 3, px: 2, fontSize: '0.875rem', lineHeight: 1.5 }}>
//             Soopra.ai is an innovative platform that empowers experts, educators, and influencers to create personalized AI personas through its proprietary Personality Engine™. By enabling users to train their AI personas with their own knowledge, personality traits, and content from sources like YouTube, articles, or podcasts, Soopra facilitates 24/7 engagement with audiences worldwide. The platform stands out for its focus on scalability and personalization, allowing creators to connect with fans and learners in real-time without sacrificing authenticity. With features like robust analytics, monetization opportunities, and the ability to own and control digital personas, Soopra transforms how professionals share expertise, making it accessible anytime, anywhere, while maintaining a consistent brand voice.
//           </Typography>

//           <Typography variant="h6" sx={{ mb: 2, px: 2 }}>
//             Key Features
//           </Typography>

//           <Box sx={{ mb: 3, px: 2 }}>
//             <Typography variant="body2" sx={{ mb: 1 }}>
//               • <strong>Personalized AI Personas</strong>: Users can train AI personas using their own content (e.g., YouTube, articles, podcasts) to reflect their expertise and personality.
//             </Typography>
//             <Typography variant="body2" sx={{ mb: 1 }}>
//               • <strong>24/7 Engagement</strong>: AI personas interact with audiences in real-time, answering questions and providing insights around the clock.
//             </Typography>
//             <Typography variant="body2" sx={{ mb: 1 }}>
//               • <strong>Monetization Opportunities</strong>: Creators can offer premium sessions, Q&As, and courses, retaining full ownership of their digital personas.
//             </Typography>
//             <Typography variant="body2" sx={{ mb: 1 }}>
//               • <strong>Robust Analytics</strong>: Detailed metrics help users understand audience needs and optimize content.
//             </Typography>
//             <Typography variant="body2" sx={{ mb: 1 }}>
//               • <strong>Soopra ASK (2.0)</strong>: A social network of AI agents capable of advanced tasks like scheduling, complex query handling, and sales outreach.
//             </Typography>
//             <Typography variant="body2" sx={{ mb: 1 }}>
//               • <strong>Security and Privacy</strong>: Built with strong guardrails to ensure trust, authenticity, and data control.
//             </Typography>
//           </Box>

//           <DynamicAdSense adType='auto'></DynamicAdSense>
//         </Box>
//       </Box>
//     </Fragment>
//   );
// };

// export default AdsDemo;

import { Fragment, useRef, useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  debounce,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  getFollowingFeed,
  getHomeFeed,
  getNewsFeed,
} from '../../../utils/apis/memeverse';
import {
  CommentType,
  PaginatedPosts,
  PostType,
  FlattenedFeedType,
} from '../memeverse_types';
import FeedTopbar from '../feed/FeedTopbar';
import Meme from '../feed/Meme';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import DetectBottomScrollDiv from '../../journaling/DetectBottomScrollDiv';
import Lottie from 'lottie-react';
import LoadingFace from '../../../assets/animations/LoadingFace.json';
import { AblyWebsocketChannelProvider } from '../../../component/AblyWebsockets/AblyWebsocketChannelProvider';
import { useAuth } from '../../../hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAskWebsockets } from '../hooks/useAskWebsockets';
import { flattenCommentsList } from '../helper';
import DynamicAdSense from './DynamicAdSense';
// import { useProfileData } from '../../../hooks/useProfileData';

const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

const Feed = ({ startingTabValue = 0 }) => {
  return (
    <AblyWebsocketChannelProvider channelName='ask'>
      <MainFeed startingTabValue={startingTabValue} />
    </AblyWebsocketChannelProvider>
  );
};

const MainFeed = ({ startingTabValue = 0 }) => {
  const [commentLimit, setCommentLimit] = useState<number>(
    JSON.parse(localStorage.getItem('comment_limit_image') || '2')
  );

  const scrollBoxRef = useRef(null);
  // const internalCount = useRef<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(
    location?.state?.tabValue || startingTabValue || 0
  );
  const { authUser, authLoading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  // const { fetchProfileDataFromUserName } = useProfileData();

  let { refresh: refreshNews } = useAskWebsockets(['memesNews', authUser?.uid]);

  let { refresh: refreshForYou } = useAskWebsockets(
    ['memesCommunity', authUser?.uid, authLoading],
    () => {
      handleTabChange(null, 1);
      scrollBoxRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant',
      }); // scroll back to top
    }
  );

  let { refresh: refreshFollowing } = useAskWebsockets([
    'memesFromFollowing',
    authUser?.uid,
  ]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const handleNavChange = ({ value }: { value: string }) => {
    navigate(`/${value}`);
  };

  useEffect(() => {
    scrollBoxRef.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    }); // scroll back to top
  }, [tabValue]);

  useEffect(() => {
    if (location.state?.scrollY) {
      scrollBoxRef.current?.scrollTo({
        top: location.state?.scrollY,
        left: 0,
        behavior: 'instant',
      });
    }
  }, [location]);

  const calcCommentLimit: () => void = () => {
    const pageHeight = window.innerHeight;
    const heightNavBar = isMobile ? 168 + 56 : 86 + 56;
    const heightPerComment = 157;
    const heightMoreCommentsButton = 24;

    const minImageHeight = isMobile ? (isXs ? 100 : 150) : 200;

    let postHeight = heightPerComment;

    let limit1 = Math.floor(
      (pageHeight - heightNavBar - postHeight - heightMoreCommentsButton) /
        heightPerComment
    );
    let limit2 = Math.floor(
      (pageHeight -
        heightNavBar -
        postHeight -
        minImageHeight -
        heightMoreCommentsButton) /
        heightPerComment
    );
    localStorage.setItem('comment_limit_no_image', JSON.stringify(limit1));
    localStorage.setItem('comment_limit_image', JSON.stringify(limit2));
    if (commentLimit !== limit1) {
      setCommentLimit(limit1);
    }
  };

  useEffect(() => {
    calcCommentLimit(); // calculate on mount
    let debouncedCalc = debounce(calcCommentLimit, 250);

    window.addEventListener('resize', debouncedCalc);
    document.addEventListener('fullscreenchange', debouncedCalc);

    return () => {
      window.removeEventListener('resize', debouncedCalc);
      document.removeEventListener('fullscreenchange', debouncedCalc);
    };
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  // const runBackgroundProfileFetch = async () => {
  //   // run background profile fetch after both feed pages are loaded
  //   if (internalCount.current >= 0) {
  //     if (internalCount.current < 2) {
  //       internalCount.current++;
  //     }
  //     if (internalCount.current === 2) {
  //       internalCount.current = -1; // make sure this doesn't run again
  //       if (authUser?.userName) {
  //         fetchProfileDataFromUserName(
  //           authUser?.userName,
  //           authUser?.uid,
  //           true,
  //           () => {}
  //         )
  //       }
  //     }
  //   }
  // }

  const {
    data: memesNews,
    // error,
    fetchNextPage: fetchMemesNews,
    hasNextPage: hasNextMemesNews,
    isFetching: isFetchingMemesNews,
    isFetchingNextPage: isFetchingNextMemesNews,
    // status,
  } = useInfiniteQuery({
    queryKey: ['memesNews', authUser?.uid],
    queryFn: async ({ pageParam }) => {
      if (authLoading) {
        return {
          pagination: {},
          posts: {},
          comments: {},
          orderedPosts: [],
        } as FlattenedFeedType;
      }
      return getNewsFeed(authUser?.uid, 10, 5, pageParam)
        .then(async (resp) => {
          // runBackgroundProfileFetch()
          if (resp.ok) {
            let data: PaginatedPosts = await resp.json();
            if (enableDevTools) console.log('news feed data: ', data);

            let posts = {} as Record<string, PostType>;
            let comments = {} as Record<string, CommentType>;
            let orderedPosts = [] as string[];
            for (let post of data.posts) {
              post.page_no = pageParam;
              posts[post.docId] = post;
              orderedPosts.push(post.docId);

              if (post.comments_list) {
                post.flat_comments_list = flattenCommentsList(
                  comments,
                  post.comments_list,
                  pageParam
                );
              } else {
                post.flat_comments_list = [];
              }
            }

            return {
              pagination: data.pagination,
              posts,
              comments,
              orderedPosts,
            } as FlattenedFeedType;
          } else {
            return {
              pagination: {},
              posts: {},
              comments: {},
              orderedPosts: [],
            } as FlattenedFeedType;
          }
        })
        .catch((err) => {
          console.log(err);
          return {
            pagination: {},
            posts: {},
            comments: {},
            orderedPosts: [],
          } as FlattenedFeedType;
        });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination.has_next) {
        return undefined;
      }
      return lastPage.pagination.next_page;
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.pagination.has_next) {
        return undefined;
      }
      return firstPage.pagination.next_page;
    },
    enabled: !authLoading,
  });

  const {
    data: memesForYou,
    // error,
    fetchNextPage: fetchMemesForYou,
    hasNextPage: hasNextMemesForYou,
    isFetching: isFetchingMemesForYou,
    isFetchingNextPage: isFetchingNextMemesForYou,
    // status,
  } = useInfiniteQuery({
    queryKey: ['memesCommunity', authUser?.uid, authLoading],
    queryFn: async ({ pageParam }) => {
      if (authLoading) {
        return {
          pagination: {},
          posts: {},
          comments: {},
          orderedPosts: [],
        } as FlattenedFeedType;
      }
      return getHomeFeed(authUser?.uid, 10, 5, pageParam, 'recent')
        .then(async (resp) => {
          // runBackgroundProfileFetch()
          if (resp.ok) {
            let data: PaginatedPosts = await resp.json();
            if (enableDevTools) console.log('main feed data: ', data);

            let posts = {} as Record<string, PostType>;
            let comments = {} as Record<string, CommentType>;
            let orderedPosts = [] as string[];
            for (let post of data.posts) {
              post.page_no = pageParam;
              posts[post.docId] = post;
              orderedPosts.push(post.docId);

              if (post.comments_list) {
                post.flat_comments_list = flattenCommentsList(
                  comments,
                  post.comments_list,
                  pageParam
                );
              } else {
                post.flat_comments_list = [];
              }
            }

            return {
              pagination: data.pagination,
              posts,
              comments,
              orderedPosts,
            } as FlattenedFeedType;
          } else {
            return {
              pagination: {},
              posts: {},
              comments: {},
              orderedPosts: [],
            } as FlattenedFeedType;
          }
        })
        .catch((err) => {
          console.log(err);
          return {
            pagination: {},
            posts: {},
            comments: {},
            orderedPosts: [],
          } as FlattenedFeedType;
        });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination.has_next) {
        return undefined;
      }
      return lastPage.pagination.next_page;
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.pagination.has_next) {
        return undefined;
      }
      return firstPage.pagination.next_page;
    },
    enabled: !authLoading,
  });

  const {
    data: memesFromFollowing,
    // error,
    fetchNextPage: fetchMemesFromFollowing,
    hasNextPage: hasNextMemesFromFollowing,
    isFetching: isFetchingMemesFromFollowing,
    isFetchingNextPage: isFetchingNextMemesFromFollowing,
    // status,
  } = useInfiniteQuery({
    queryKey: ['memesFromFollowing', authUser?.uid],
    queryFn: async ({ pageParam }) => {
      if (!authUser?.uid) {
        return {
          pagination: {},
          posts: {},
          comments: {},
          orderedPosts: [],
        } as FlattenedFeedType;
      }
      return getFollowingFeed(authUser?.uid, 10, pageParam)
        .then(async (resp) => {
          // runBackgroundProfileFetch();
          if (resp.ok) {
            let data: PaginatedPosts = await resp.json();
            if (enableDevTools) console.log('following feed data: ', data);

            let posts = {} as Record<string, PostType>;
            let comments = {} as Record<string, CommentType>;
            let orderedPosts = [] as string[];
            for (let post of data.posts) {
              post.page_no = pageParam;
              posts[post.docId] = post;
              orderedPosts.push(post.docId);

              if (post.comments_list) {
                post.flat_comments_list = flattenCommentsList(
                  comments,
                  post.comments_list,
                  pageParam
                );
              } else {
                post.flat_comments_list = [];
              }
            }

            return {
              pagination: data.pagination,
              posts,
              comments,
              orderedPosts,
            } as FlattenedFeedType;
          } else {
            return {
              pagination: {},
              posts: {},
              comments: {},
              orderedPosts: [],
            } as FlattenedFeedType;
          }
        })
        .catch((err) => {
          console.log(err);
          return {
            pagination: {},
            posts: {},
            comments: {},
            orderedPosts: [],
          } as FlattenedFeedType;
        });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination.has_next) {
        return undefined;
      }
      return lastPage.pagination.next_page;
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.pagination.has_next) {
        return undefined;
      }
      return firstPage.pagination.next_page;
    },
    enabled: !authLoading,
  });

  // Compile Infinite Query Data into something more usable
  const { newsPosts, newsComments, newsOrderedPosts } = useMemo(() => {
    const newsPosts: Record<string, PostType> = {};
    const newsComments: Record<string, CommentType> = {};
    let newsOrderedPosts: string[] = [];

    if (memesNews?.pages) {
      memesNews.pages.forEach((page) => {
        Object.assign(newsPosts, page.posts);
        Object.assign(newsComments, page.comments);
        newsOrderedPosts = [...newsOrderedPosts, ...page.orderedPosts];
      });
    }

    // Dedupe just in case
    newsOrderedPosts = Array.from(new Set(newsOrderedPosts));

    return { newsPosts, newsComments, newsOrderedPosts };
  }, [memesNews, refreshNews]); //eslint-disable-line react-hooks/exhaustive-deps

  const { forYouPosts, forYouComments, forYouOrderedPosts } = useMemo(() => {
    const forYouPosts: Record<string, PostType> = {};
    const forYouComments: Record<string, CommentType> = {};
    let forYouOrderedPosts: string[] = [];

    if (memesForYou?.pages) {
      memesForYou.pages.forEach((page) => {
        Object.assign(forYouPosts, page.posts);
        Object.assign(forYouComments, page.comments);
        forYouOrderedPosts = [...forYouOrderedPosts, ...page.orderedPosts];
      });
    }

    // Dedupe just in case
    forYouOrderedPosts = Array.from(new Set(forYouOrderedPosts));

    return { forYouPosts, forYouComments, forYouOrderedPosts };
  }, [memesForYou, refreshForYou]); //eslint-disable-line react-hooks/exhaustive-deps

  const { followingPosts, followingComments, followingOrderedPosts } =
    useMemo(() => {
      const followingPosts: Record<string, PostType> = {};
      const followingComments: Record<string, CommentType> = {};
      let followingOrderedPosts: string[] = [];

      if (memesFromFollowing?.pages) {
        memesFromFollowing.pages.forEach((page) => {
          Object.assign(followingPosts, page.posts);
          Object.assign(followingComments, page.comments);
          followingOrderedPosts = [
            ...followingOrderedPosts,
            ...page.orderedPosts,
          ];
        });
      }

      // Dedupe just in case
      followingOrderedPosts = Array.from(new Set(followingOrderedPosts));
      return { followingPosts, followingComments, followingOrderedPosts };
    }, [memesFromFollowing, refreshFollowing]); //eslint-disable-line react-hooks/exhaustive-deps

  // Check if following feed is empty
  const isFollowingEmpty =
    tabValue === 2 &&
    !isFetchingMemesFromFollowing &&
    followingOrderedPosts.length === 0;

  // Choose which list to display based on the tab value
  const memesToDisplay: FlattenedFeedType =
    tabValue === 0
      ? {
          pagination: null,
          posts: newsPosts,
          comments: newsComments,
          orderedPosts: newsOrderedPosts,
        }
      : tabValue === 1
      ? {
          pagination: null,
          posts: forYouPosts,
          comments: forYouComments,
          orderedPosts: forYouOrderedPosts,
        }
      : {
          pagination: null,
          posts: followingPosts,
          comments: followingComments,
          orderedPosts: followingOrderedPosts,
        };

  const loadNextPage = () => {
    if (enableDevTools) console.log('Loading next page');
    if (tabValue === 0) {
      if (
        !isFetchingMemesNews &&
        !isFetchingNextMemesNews &&
        hasNextMemesNews
      ) {
        fetchMemesNews();
      }
    } else if (tabValue === 1) {
      if (
        !isFetchingMemesForYou &&
        !isFetchingNextMemesForYou &&
        hasNextMemesForYou
      ) {
        fetchMemesForYou();
      }
    } else {
      // temp, add following memes
      if (
        !isFetchingMemesFromFollowing &&
        !isFetchingNextMemesFromFollowing &&
        hasNextMemesFromFollowing
      ) {
        fetchMemesFromFollowing();
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        scrollBoxRef.current?.scrollBy({
          top: window.innerHeight,
          behavior: 'smooth',
        });
      } else if (event.key === 'ArrowUp') {
        scrollBoxRef.current?.scrollBy({
          top: -window.innerHeight,
          behavior: 'smooth',
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        // px: {xs: 0, md: 4},
      }}
    >
      {/* <Box
        sx={{
          height: 'auto',
          flexShrink: 0,
          my: { xs: 0, md: 1.25 },
          boxShadow: { xs: 'none', md: '0 1px 4px rgba(0, 0, 0, 0.1)' },
          borderRadius: {
            xs: 0,
            md: 3,
          },
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
          scrollMarginTop: '10px',
          maxWidth: 'md',
          width: '100%',
        }}
      >
        <FeedTopbar
          tabValue={tabValue}
          handleTabChange={handleTabChange}
          handleNavChange={handleNavChange}
          scrollBoxRef={scrollBoxRef}
        />
      </Box> */}

      <Box
        id='ask-scroll-box'
        className={`tabValue-${tabValue}`}
        ref={scrollBoxRef}
        sx={{
          display: 'flex',
          flexGrow: 1,
          scrollSnapType: 'y mandatory',
          overflow: 'auto',
          width: '100%',
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            height: '100%',
            maxWidth: 'md',
            margin: '0 auto',
            maxHeight: '100%',
          }}
        >
          {isFollowingEmpty && (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                px: 2,
                gap: 1,
              }}
            >
              <Typography variant='h7'>You’re not following anyone.</Typography>

              <Typography variant='body1' color='text.secondary'>
                You will see posts from experts you follow here.
              </Typography>

              <Button
                variant='contained'
                onClick={() => handleTabChange(null, 0)}
              >
                View For You Page
              </Button>
            </Box>
          )}
          {/* Render the list of meme posts */}

          {memesToDisplay.orderedPosts.map((postId, ind) => (
            <>
              <Fragment
                key={`post-fragment-${postId}-${memesToDisplay.posts[postId].comments_counts}`}
              >
                <Meme
                  post={memesToDisplay.posts[postId]}
                  allComments={memesToDisplay.comments}
                />
                {ind === memesToDisplay.orderedPosts.length - 5 && (
                  <DetectBottomScrollDiv
                    onReachBottom={loadNextPage}
                    isLoading={
                      isFetchingMemesForYou || isFetchingNextMemesForYou
                    }
                    isFullyLoaded={!hasNextMemesForYou}
                    rootMargin='100px'
                    className='tiny-infinite-scroll'
                  />
                )}
              </Fragment>
              {ind % 4 === 0 && ind !== 0 && (
                <Fragment
                  key={`post-fragment-${postId}-${memesToDisplay.posts[postId].comments_counts}-ad`}
                >
                  <DynamicAdSense adType='auto' />
                </Fragment>
              )}
            </>
          ))}

          {(isFetchingMemesForYou || isFetchingNextMemesForYou) && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '& path': {
                  fill: (theme) => theme.palette.primary.main,
                },
              }}
            >
              <Lottie
                animationData={LoadingFace}
                loop={true}
                style={{
                  width: '100px',
                  height: '100px',
                }}
              />
            </Box>
          )}
          <DetectBottomScrollDiv
            onReachBottom={loadNextPage}
            isLoading={isFetchingMemesForYou || isFetchingNextMemesForYou}
            isFullyLoaded={!hasNextMemesForYou}
            rootMargin='400px'
            className='infinite-scroll-bottom'
          />
        </Box>

        {
          <Box
            sx={{
              width: '48px',
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              position: 'fixed',
              right: 24,
              top: 0,
              bottom: 0,
            }}
          >
            <Button
              variant='outlined'
              color='primary'
              size='small'
              onClick={() => {
                scrollBoxRef.current?.scrollBy({
                  top: -window.innerHeight,
                  behavior: 'smooth',
                });
              }}
              sx={{
                minWidth: '0px',
              }}
            >
              <ArrowUpward />
            </Button>
            <Button
              variant='outlined'
              color='primary'
              size='small'
              onClick={() => {
                scrollBoxRef.current?.scrollBy({
                  top: window.innerHeight,
                  behavior: 'smooth',
                });
              }}
              sx={{
                minWidth: '0px',
              }}
            >
              <ArrowDownward />
            </Button>
          </Box>
        }
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
          textAlign={'center'}
          sx={{ p: 1 }}
        >
          Soopra AI agents: Beta phase, no affiliation, without endorsement.
        </Typography>
      </Box>
    </Box>
  );
};

export default Feed;
